import { authorAnia, authorKasia } from 'data/authors';
import Link from 'components/ui/link';
import React from 'react';
import cover from 'img/user-experience/cover/userexperience1.png';

export const data = [
  {
    url: '/user-experience/',
    title: 'Introduction',
    desc: 'How to Create a Great User Experience? All You Need to Know',
    metaDesc:
      'Today all customers demand a great user experience and are willing to pay extra for it. Learn everything you should know about creating great UX.',
    timeToRead: 14,
    author: authorAnia,
    date: 'June 09, 2020',
    heading: (
      <>
        <p>
          User experience is a buzzword that has dominated the business world for years. And there
          is a good reason for it – according to{' '}
          <Link
            href="https://www.salesforce.com/research/customer-expectations/"
            rel="noopener noreferrer nofollow"
            target="_blank"
          >
            Salesforce
          </Link>
          , 67% of customers say they are willing to pay more for a better user experience. This
          gives businesses a great opportunity to stand out from the crowd. While products are easy
          to imitate, great experiences aren’t.
        </p>
        <p>
          In the following guide, we’re going to explore what UX experience is, how to create an
          effective UX design process and what it should include, as well as tell you about
          conducting UX research and creating UX prototypes. Let’s dive in.
        </p>
      </>
    ),
    button: {
      text: 'Improve user experience',
    },
    cover,
  },
  {
    url: '/user-experience/what-is-ux/',
    title: 'Chapter 1',
    desc: 'What is UX? 10 Experts Share Their Definition',
    metaDesc: `There are many ways of looking at the user experience definition – some are far from the standard Wikipedia answer! Read on to find out how 10 experts describe what UX means to them. `,
    timeToRead: 7,
    author: authorKasia,
    date: 'June 09, 2020',
    heading: (
      <>
        <p>
          Did you realize that every{' '}
          <Link
            href="https://www.forrester.com/report/The+Six+Steps+For+Justifying+Better+UX/-/E-RES117708"
            rel="noopener noreferrer nofollow"
            target="_blank"
          >
            $1 invested in user experience has a return of $100
          </Link>
          ? This translates into a ROI of 9,900% – seems like a pretty good investment to us!
          However, before you start spending your hard-earned money, it’s worth understanding what
          user experience design is.
        </p>
        <p>
          For this purpose, we’ve decided to talk to 10 business experts to tell us – what is UX?
          Take a look at their insights.{' '}
        </p>
      </>
    ),
    button: {
      text: 'Improve user experience',
    },
    cover,
  },
  {
    url: '/user-experience/ux-design-process/',
    title: 'Chapter 2',
    metaDesc: `Having an effective UX design process in place is necessary for building products that will meet customer expectations. Learn how to implement one. `,
    desc: 'UX Design Process: What to Include and How to Do It Right',
    timeToRead: 8,
    author: authorAnia,
    date: 'June 09, 2020',
    heading: (
      <>
        <p>
          Customers become ever more demanding, and it’s hardly surprising. With the number of
          alternatives available to them, they can afford to be picky. In fact,{' '}
          <Link
            href="https://www.toptal.com/designers/ux/ux-statistics-insights-infographic"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            90% of app users
          </Link>{' '}
          stop using an app due to poor performance. Is there a way to ensure this doesn’t happen to
          you? Of course, there is, provided that you have an effective UX design process in place.
        </p>
        <p>
          In the following article, we’re going to talk about the importance of UX design, discuss
          the most crucial user experience elements as well as guide you through creating an
          efficient UX design process.
        </p>
      </>
    ),
    button: {
      text: 'Improve user experience',
    },
    cover,
  },
  {
    url: '/user-experience/user-experience-design/',
    title: 'Chapter 3',
    desc: 'User Experience Design: How to Improve and Best Practices',
    metaDesc: `One of the keys to a successful business lies in effective user experience design. Read on to learn UX best practices & see how you can improve your product.`,
    timeToRead: 9,
    author: authorAnia,
    date: 'June 11, 2020',
    heading: (
      <>
        <h2>The Key to Launching a Successful Product</h2>
        <p>
          It’s a proven fact that UX design can make or break a business by heavily impacting its
          customer loyalty levels. In fact, according to{' '}
          <Link
            href="https://www.pwc.com/us/en/advisory-services/publications/consumer-intelligence-series/pwc-consumer-intelligence-series-customer-experience.pdf"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            PWC
          </Link>
          , 82% of top-performing businesses admit that they prioritize usability across their
          digital touchpoints.
        </p>
        <p>
          By taking your audience’s experience and opinions into account, you raise their
          satisfaction and increase the number of users purchasing your services or products. It’s
          something you can actively improve by surveying your audience and researching their
          preferences.
        </p>
        <p>
          In the following article, we discuss several best practices to improve user experience and
          share examples of rich user experience implementations.
        </p>
      </>
    ),
    button: {
      text: 'Improve user experience',
    },
    cover,
  },

  {
    url: '/user-experience/user-journey-map/',
    title: 'Chapter 4',
    desc: 'How to develop a user journey map in 6 simple steps',
    metaDesc: `Developing a user journey map is a necessity for creating a great user experience. Read this article to learn how. `,
    timeToRead: 8,
    author: authorKasia,
    date: 'June 09, 2020',
    heading: (
      <>
        <p>
          Launching a product or service is an exciting process. Every product owner wants to turn
          first-time buyers into repeat customers. However, this requires a deeper understanding of
          who they are and what they need. Luckily, by going through the process of creating a user
          journey map, you can design in a way that gets your users motivated, and provide them with
          the results they desire to achieve with your product.
        </p>
        <p>
          Before we start creating a user journey map, it is important to understand what a user
          journey entails. So, let us now peek into the definition of a user journey and what it
          means.
        </p>
      </>
    ),
    button: {
      text: 'Improve user experience',
    },
    cover,
  },
  {
    url: '/user-experience/ux-prototyping/',
    title: 'Chapter 5',
    desc: 'All You Need to Know About Effective UX Prototyping',
    metaDesc: `UX prototyping is the best way of ensuring you meet your users’ expectations. Read on to learn about its various benefits, methodologies, and how it can help your product.`,
    timeToRead: 8,
    author: authorKasia,
    date: 'June 09, 2020',
    heading: (
      <>
        <h2>The Key to Effective UX Prototyping</h2>
        <p>
          According to recent UX{' '}
          <Link
            href="https://www.invisionapp.com/inside-design/statistics-on-user-experience/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            statistics
          </Link>
          , first impressions are 94% design related. As you can only make a first impression on
          your users once, it better be a good one!
        </p>
        <p>
          One way of ensuring that your product or service is the best it can possibly be is by
          leveraging UX prototyping.
        </p>
        <p>
          In the following article, we will tackle UX Prototyping inside out – from its benefits and
          various methodologies, all the way through to the different types of prototyping
          approaches available.
        </p>
      </>
    ),
    button: {
      text: 'Improve user experience',
    },
    cover,
  },
  {
    url: '/user-experience/ux-research/',
    title: 'Chapter 6',
    desc: 'How to Conduct Effective UX Research?',
    metaDesc:
      'Conducting mobile usability tests is necessary to ensure your users return to the app. Find out how to test your apps effectively and what tools to use.', // to change
    timeToRead: 7,
    author: authorAnia,
    date: 'June 09, 2020',
    button: {
      text: 'Improve user experience',
    },
    heading: (
      <>
        <p>
          According to a report from{' '}
          <Link
            href="https://www.forbes.com/sites/forbestechcouncil/2017/01/23/how-ux-is-transforming-business-whether-you-want-it-to-or-not/#6e9a1a0c580e"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Forrester
          </Link>
          , on average, every dollar invested in UX brings 100 dollars in return. This translates to
          a staggering 9,900% in Return on Investment (ROI). However, in order to ensure that your
          product’s UX is effective, you must prioritize thorough user experience research. It is an
          essential element of the UX process, which allows you to find the true value of your
          products or services, and gauge whether they will sell in the real world or not.
        </p>
        <p>
          In this article, we are going to take a closer look at user experience research, its
          various types, and the different methodologies you can use to conduct these studies.
        </p>
      </>
    ),
    cover,
  },
];
